import React, {useEffect, useState} from 'react';
import {
  Autocomplete,
  TextField,
  Typography, lighten, darken, Button
} from '@mui/material';
import Box from '@mui/material/Box';
import api from '../../../services/api';
import CircularProgress from '@mui/material/CircularProgress';
import LargePopper from '../assets/LargePopper';
import {styled} from "@mui/material/styles";
import {SlidingPaneAddNew} from "../../Page/SlidingPaneAddNew";
import AddIcon from "@mui/icons-material/Add";

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor: lighten(theme.palette.primary.light, 0.85),
  ...theme.applyStyles('dark', {
    backgroundColor: darken(theme.palette.primary.main, 0.8),
  }),
}));

const GroupItems = styled('ul')({
  padding: 0,
});


export default function SingleSelectAddressMUI({
  params,
  column,
}: {
  params: any;
  column: any;
}) {
  const [selectOptions, setSelectOptions] = useState<[]>([]);
  const [addingEntity, setAddingEntity] = useState(false);
  const [loading, setLoading] = useState(false);
  const transformedValue = params.value;

  const [openedSlidingPaneAddNew, setOpenedSlidingPaneAddNew] = useState<any>({
    schema: column?.tempSchema,
    row: {},
  });

  const fetchSelectOptions = async (searchText: string) => {
    setLoading(true);

    const searchParams = new URLSearchParams({
      searchValue: searchText,
    });

    if (column?.fieldType === 'address'){
      if (params?.row?.group_id){
        searchParams.set('group_id', params?.row?.group_id);
      }else if (params?.row?.group?.id){
        searchParams.set('group_id', params?.row?.group?.id);
      }
    }

    if (column?.url) {
      api.get(`/${column?.url}/names`, searchParams).then((result: any) => {
        if (result?.data) {
          result.data.unshift({ id: 0, name: '', isButton: true });
          setSelectOptions(result.data);
        } else {
          result.unshift({ id: 0, name: '', isButton: true });
          setSelectOptions(result);
        }
        setLoading(false);
      });
    }
  };

  const onChange = (event: any, value: any) => {
    if (value?.isButton) {
      setOpenedSlidingPaneAddNew({...openedSlidingPaneAddNew, row: params?.row});
    } else {
      params.api.setEditCellValue({
        id: params.id,
        field: params.field,
        value: value,
      });
    }
  };

  const [searchText, setSearchText] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);

  useEffect(() => {

    if (searchText === '') {
      setFilteredOptions(selectOptions); // Ha nincs keresés, minden opció látható
    } else {
      setFilteredOptions(
        selectOptions.filter((option: any) => {
          const nameMatches = option?.name
            ? option.name.toLowerCase().includes(searchText.toLowerCase())
            : false;

          const addressMatches = option?.address_line1
            ? option.address_line1.toLowerCase().includes(searchText.toLowerCase())
            : false;

          return nameMatches || addressMatches;
        })
      );
    }
  }, [searchText, selectOptions]);

  return (
    <>
      <Autocomplete
        sx={{
          '& .MuiOutlinedInput-root': {
            padding: '0',
            paddingRight: '15px !important',
          },
          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            paddingTop: '0px',
            paddingBottom: '0px',
          },
          '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
            right: '0px',
          },
        }}
        groupBy={(option: any) => {
          let groupName: any = option?.group;

          if (option?.isButton) {
            groupName = null;
          }

          return groupName;
        }}
        renderGroup={(params: any) => {
          return (
            <li key={params.key}>
              {params.group && <GroupHeader>{params.group}</GroupHeader>}
              <GroupItems>{params.children}</GroupItems>
            </li>
          );
        }}
        componentsProps={{
          clearIndicator: {
            sx: {
              display: 'none',
            },
          },
        }}
        PopperComponent={LargePopper}
        freeSolo
        onOpen={() => fetchSelectOptions('')}
        fullWidth={true}
        size="small"
        loading={loading}
        options={filteredOptions}
        renderOption={(props: any, option: any) => {
          return (
            <Box
              component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
              {...props}
            >
              {option?.isButton && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  size={'small'}
                  fullWidth
                >
                  Add new
                </Button>
              )}
              {option?.color && typeof option?.color === 'string' ? (
                <></>
              ) : (
                <Typography
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {option.name ? (
                    <>
                      <Typography>{option?.name} </Typography>
                      {option?.sub_title && (
                        <Typography color={'#666'}>
                          {' '}
                          ({option?.sub_title})
                        </Typography>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography>{option?.sub_title}</Typography>
                    </>
                  )}
                </Typography>
              )}
            </Box>
          );
        }}
        getOptionLabel={(option) => {
          if (option?.name) {
            return option.name;
          }

          if (
            option?.zip_code ||
            option?.city ||
            option?.address_line1 ||
            option?.address_line2
          ) {
            return `${option?.zip_code} ${option?.city} ${option?.address_line1} ${option?.address_line2}`;
          }

          return option.name;
        }}
        getOptionKey={(option) => option?.id}
        isOptionEqualToValue={(option, value) => {
          return option.id === value.id;
        }} // Use the new prop name and type
        value={transformedValue?.disabled ? null : transformedValue}
        onChange={(event, newValue) => {
          onChange(event, newValue);
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              /*label="Type here..."*/
              size="small"
              InputLabelProps={{
                style: {
                  fontSize: 13,
                  paddingTop: 2,
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress
                        color="inherit"
                        size={20}
                        className={
                          'absolute right-[9px] text-[#b7b7b7] top-[1px]'
                        }
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') {
            setSearchText(newInputValue);
          }
        }}
        disabled={transformedValue?.disabled}
      />

      {openedSlidingPaneAddNew?.row?.id && (
        <>
          <SlidingPaneAddNew
            schema={openedSlidingPaneAddNew?.schema}
            id={
              openedSlidingPaneAddNew?.hasOwnPane
                ? undefined
                : openedSlidingPaneAddNew?.row?.id
            }
            onSubmit={() => {
              setOpenedSlidingPaneAddNew({
                schema: column?.tempSchema,
                row: {},
              });
            }}
            propertiesFromTable={
              openedSlidingPaneAddNew?.row ? openedSlidingPaneAddNew?.row : {}
            }
            onClose={(value) => {
              setOpenedSlidingPaneAddNew({
                schema: column?.tempSchema,
                row: {},
              });
            }}
          />
        </>
      )}

    </>
  );
}
